/**
 * Document Scope Settings Panel
 * Configure which document scopes the AI Assistant can access
 * Includes warning modal for enabling prior projects access
 */

import { useState, useEffect } from 'react';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Label, Input } from '@/components/ui';

interface DocumentScopeSettingsProps {
  allowedScopes: string[];
  allowedProjectIds: string[];
  availableProjects?: Array<{ projectId: string; projectCode: string; name: string }>;
  onChange: (scopes: string[], projectIds: string[]) => void;
  onClose: () => void;
  inProjectContext: boolean;
}

export default function DocumentScopeSettings({
  allowedScopes,
  allowedProjectIds,
  availableProjects = [],
  onChange,
  onClose,
  inProjectContext,
}: DocumentScopeSettingsProps) {
  const [localScopes, setLocalScopes] = useState<string[]>(allowedScopes);
  const [localProjectIds, setLocalProjectIds] = useState<string[]>(allowedProjectIds);
  const [showPriorProjectsModal, setShowPriorProjectsModal] = useState(false);
  
  // Update local state when props change
  useEffect(() => {
    setLocalScopes(allowedScopes);
    setLocalProjectIds(allowedProjectIds);
  }, [allowedScopes, allowedProjectIds]);
  
  const handleScopeToggle = (scope: string) => {
    if (scope === 'prior-projects') {
      if (!localScopes.includes(scope)) {
        // Enabling prior projects - show warning modal
        setShowPriorProjectsModal(true);
      } else {
        // Disabling prior projects
        const newScopes = localScopes.filter(s => s !== scope);
        setLocalScopes(newScopes);
        setLocalProjectIds([]);
        onChange(newScopes, []);
      }
    } else {
      // Toggle other scopes directly (though standards and company are typically always on)
      const newScopes = localScopes.includes(scope)
        ? localScopes.filter(s => s !== scope)
        : [...localScopes, scope];
      setLocalScopes(newScopes);
      onChange(newScopes, localProjectIds);
    }
  };
  
  const handleEnablePriorProjects = () => {
    // Only enable if user has selected at least one project
    if (localProjectIds.length > 0) {
      const newScopes = [...localScopes, 'prior-projects'];
      setLocalScopes(newScopes);
      onChange(newScopes, localProjectIds);
      setShowPriorProjectsModal(false);
    }
  };
  
  const handleCancelPriorProjects = () => {
    setLocalProjectIds([]);
    setShowPriorProjectsModal(false);
  };
  
  const handleProjectToggle = (projectId: string) => {
    const newProjectIds = localProjectIds.includes(projectId)
      ? localProjectIds.filter(id => id !== projectId)
      : [...localProjectIds, projectId];
    setLocalProjectIds(newProjectIds);
  };
  
  const scopeInfo = [
    {
      id: 'current-project',
      label: 'Current Project',
      icon: '📄',
      description: 'Search documents in the current project only',
      locked: inProjectContext,
      recommended: true,
    },
    {
      id: 'industry-standards',
      label: 'Industry Standards',
      icon: '📚',
      description: 'DO-178C, DO-254, AS9100D, DO-160G, etc.',
      locked: true, // Always on
      recommended: true,
    },
    {
      id: 'company-documents',
      label: 'Company Policies & Templates',
      icon: '📋',
      description: 'Internal policies, procedures, and templates',
      locked: true, // Always on
      recommended: true,
    },
    {
      id: 'prior-projects',
      label: 'Prior Projects',
      icon: '⚠',
      description: 'Reference similar work from your other projects',
      locked: false,
      recommended: false,
      warning: true,
    },
  ];
  
  return (
    <>
      <div className="bg-popover border border-border rounded-lg p-4 shadow-xl mb-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Document Access Scopes</h3>
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-white"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>
        
        <p className="text-sm text-muted-foreground mb-4">
          Control which documents the AI Assistant can search. Green = Safe defaults.
        </p>
        
        <div className="space-y-3">
          {scopeInfo.map(scope => {
            const isEnabled = localScopes.includes(scope.id);
            const isDisabled = scope.locked && isEnabled;
            
            return (
              <div
                key={scope.id}
                className={`p-3 rounded border ${
                  scope.warning
                    ? 'border-warning/30 bg-warning-light0/5'
                    : 'border-border bg-popover/50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{scope.icon}</span>
                      <span className="font-medium text-white">{scope.label}</span>
                      {scope.recommended && (
                        <span className="text-xs bg-success text-success-foreground px-2 py-0.5 rounded">
                          Recommended
                        </span>
                      )}
                      {scope.locked && (
                        <span className="text-xs text-muted-foreground">
                          Always enabled
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{scope.description}</p>
                  </div>
                  
                  <Button
                    onClick={() => !isDisabled && handleScopeToggle(scope.id)}
                    disabled={isDisabled}
                    variant={isEnabled ? (scope.warning ? 'warning' : 'success') : 'outline'}
                    size="sm"
                    className={!isEnabled ? 'border-white/30 bg-card/20 hover:bg-card/30' : ''}
                  >
                    {isEnabled ? 'Enabled' : 'Enable'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mt-4 p-3 bg-info/10 border border-primary/30 rounded">
          <p className="text-sm text-link">
            <strong>Data Isolation:</strong> Current project documents are strictly isolated.
            The AI can only access your current project data plus the scopes you enable here.
          </p>
        </div>
      </div>
      
      {/* Prior Projects Warning Modal */}
      <Dialog open={showPriorProjectsModal} onOpenChange={(open) => !open && handleCancelPriorProjects()}>
        <DialogContent className="max-w-[600px] bg-popover border-warning">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-white">
              <span className="text-3xl">⚠</span>
              Enable Access to Prior Projects?
            </DialogTitle>
          </DialogHeader>
          
          <div>
            <p className="text-muted-foreground mb-4">
              This will allow the AI Assistant to search across your other projects.
              This should only be used when referencing similar past work, templates,
              or approaches from previous projects.
            </p>
            <p className="text-warning text-sm mb-4">
              <strong>Important:</strong> The AI will clearly indicate which project
              information came from, and you must explicitly select which projects to access.
            </p>
          </div>
          
          <div className="mb-6">
            <Label className="block text-sm font-medium text-muted-foreground mb-2">
              Select Projects to Access:
            </Label>
            
            {availableProjects.length === 0 ? (
              <p className="text-muted-foreground text-sm italic py-4">
                No other projects available. You need at least one other project to enable this feature.
              </p>
            ) : (
              <div className="max-h-[200px] overflow-y-auto space-y-2 bg-popover p-3 rounded border border-border">
                {availableProjects.map(project => (
                  <Label
                    key={project.projectId}
                    className="flex items-center gap-3 p-2 hover:bg-muted rounded cursor-pointer"
                  >
                    <Input
                      type="checkbox"
                      checked={localProjectIds.includes(project.projectId)}
                      onChange={() => handleProjectToggle(project.projectId)}
                      className="w-4 h-4"
                    />
                    <div>
                      <div className="text-white font-medium">
                        {project.projectCode}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {project.name}
                      </div>
                    </div>
                  </Label>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={handleCancelPriorProjects}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={handleEnablePriorProjects}
              disabled={localProjectIds.length === 0}
              className="flex-1"
            >
              Confirm ({localProjectIds.length} project{localProjectIds.length !== 1 ? 's' : ''})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

